/**
 * Set who answers for one product under the CRA. Saves one
 * `ProduktVerantwortung` card through the generic Stammdaten CRUD — created on
 * the first save, updated afterwards.
 */

import { useMemo, useState, type FormEvent, type ReactNode } from 'react';

import EmptyState from '../../components/EmptyState';
import { Modal } from '../../components/Modal';
import Spinner from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { createCmdbCard, deleteCmdbCard, errorMessage, updateCmdbCard } from '../../lib/api';
import type {
  CmdbLookupValue,
  ProduktVerantwortungValues,
  PsirtProductRow,
  PsirtTeam,
  PsirtThresholds,
} from '../../lib/types';

import PsirtChecklist from './PsirtChecklist';
import {
  KATEGORIE_FUNDSTELLE,
  KATEGORIE_ORDER,
  KATEGORIE_SHORT,
  PRODUCT_KEYS,
  addYears,
  dayValue,
  emailProblem,
  filled,
  fmtDayIso,
  fmtSpan,
  monthsBetween,
  productChecklist,
  stateName,
  toBody,
  todayIso,
} from './psirtModel';

export interface ProductFormModalProps {
  row: PsirtProductRow;
  teams: readonly PsirtTeam[];
  kategorien: readonly CmdbLookupValue[];
  kategorieFolgen: Readonly<Record<string, string>>;
  thresholds: PsirtThresholds;
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => void;
  onCreateTeam: () => void;
}

type TextKey = Exclude<keyof ProduktVerantwortungValues, 'ProduktTypeID' | 'PsirtTeamID' | 'CraKategorie'>;

export function ProductFormModal({
  row,
  teams,
  kategorien,
  kategorieFolgen,
  thresholds,
  canWrite,
  onClose,
  onSaved,
  onCreateTeam,
}: ProductFormModalProps): ReactNode {
  const toast = useToast();
  const [values, setValues] = useState<ProduktVerantwortungValues>(() => {
    const initial = row.values ?? {};
    return {
      ...initial,
      // A single team is the obvious default for a product without one.
      PsirtTeamID: initial.PsirtTeamID ?? (teams.length === 1 ? (teams[0]?.id ?? null) : null),
      SupportEnde: dayValue(initial.SupportEnde),
      GeprueftAm: dayValue(initial.GeprueftAm),
    };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const readOnly = !canWrite || saving;
  const team = teams.find((entry) => entry.id === values.PsirtTeamID) ?? null;
  const checklist = useMemo(() => productChecklist(values, team?.values ?? null), [values, team]);

  const set = (key: TextKey, value: string): void => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const problems: Partial<Record<TextKey, string | null>> = {
    OwnerEmail: emailProblem(values.OwnerEmail),
    VertretungEmail: emailProblem(values.VertretungEmail),
  };
  const hasProblems = Object.values(problems).some(Boolean);

  // --- Support period helpers ---------------------------------------------
  const today = todayIso();
  const minYears = thresholds.support_min_years;
  const basis = row.erstes_release ?? null;
  const supportEnde = filled(values.SupportEnde) ? String(values.SupportEnde) : null;
  const spanFromRelease = basis && supportEnde ? monthsBetween(basis, supportEnde) : null;
  const remaining = supportEnde ? monthsBetween(today, supportEnde) : null;
  const tooShort = spanFromRelease !== null && spanFromRelease < minYears * 12;

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (hasProblems || readOnly) return;
    setSaving(true);
    setError(null);
    try {
      const body = toBody(values, PRODUCT_KEYS);
      if (row.verantwortung_id !== null) {
        await updateCmdbCard('ProduktVerantwortung', row.verantwortung_id, body);
      } else {
        await createCmdbCard('ProduktVerantwortung', { ...body, ProduktTypeID: row.produkt_id });
      }
      toast.success(`Verantwortlichkeit für ${row.produkt} gespeichert.`);
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
      setSaving(false);
    }
  }

  async function remove(): Promise<void> {
    if (row.verantwortung_id === null) return;
    setSaving(true);
    setError(null);
    try {
      await deleteCmdbCard('ProduktVerantwortung', row.verantwortung_id);
      toast.success(`Verantwortlichkeit für ${row.produkt} entfernt.`);
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
      setSaving(false);
      setConfirmDelete(false);
    }
  }

  const text = (
    key: TextKey,
    label: string,
    options: { type?: string; placeholder?: string; hint?: ReactNode } = {},
  ): ReactNode => {
    const id = `psirt-product-${key}`;
    const problem = problems[key];
    return (
      <div className="field">
        <label htmlFor={id}>{label}</label>
        <input
          id={id}
          type={options.type ?? 'text'}
          value={String(values[key] ?? '')}
          placeholder={options.placeholder}
          disabled={readOnly}
          aria-invalid={Boolean(problem) || undefined}
          onChange={(event) => set(key, event.target.value)}
        />
        {options.hint ? <span className="hint">{options.hint}</span> : null}
        {problem ? <span className="err">{problem}</span> : null}
      </div>
    );
  };

  const teamGaps = team
    ? [
        !filled(team.values.MeldeEmail) ? 'Meldestelle' : null,
        !filled(team.values.CvdPolicyUrl) ? 'CVD-Policy' : null,
        !filled(team.values.CsirtKoordinator) || !filled(team.values.MelderEmail) ? 'Meldeweg' : null,
      ].filter((gap): gap is string => gap !== null)
    : [];

  return (
    <Modal
      title={row.verantwortung_id === null ? 'Verantwortlichkeit festlegen' : 'Verantwortlichkeit bearbeiten'}
      subtitle={
        <>
          <strong>{row.produkt}</strong>
          {row.modellreihe ? ` · ${row.modellreihe}` : ''}
          {row.instanzen > 0
            ? ` · ${row.instanzen} ausgelieferte Instanz${row.instanzen === 1 ? '' : 'en'} bei ${row.kunden} Kunde${row.kunden === 1 ? '' : 'n'}`
            : ''}
        </>
      }
      size="wide"
      dismissable={!saving}
      onClose={onClose}
      footer={
        <>
          {row.verantwortung_id !== null && canWrite ? (
            confirmDelete ? (
              <button type="button" className="danger" onClick={() => void remove()} disabled={saving}>
                Wirklich entfernen?
              </button>
            ) : (
              <button type="button" className="ghost" onClick={() => setConfirmDelete(true)} disabled={saving}>
                Zuordnung entfernen
              </button>
            )
          ) : null}
          <span className="spacer" />
          <button type="button" onClick={onClose} disabled={saving}>
            {canWrite ? 'Abbrechen' : 'Schließen'}
          </button>
          {canWrite ? (
            <button type="submit" className="primary" form="psirt-product-form" disabled={saving}>
              {saving ? <Spinner variant="on-accent" label="Speichert…" /> : 'Speichern'}
            </button>
          ) : null}
        </>
      }
    >
      <form id="psirt-product-form" className="psirt-form" onSubmit={submit} noValidate>
        <div className="psirt-form-main">
          {error ? (
            <div className="banner error" role="alert">
              {error}
            </div>
          ) : null}

          <section className="psirt-section">
            <header>
              <h3>Zuständigkeit</h3>
            </header>
            <div className="psirt-grid">
              <div className="field psirt-span">
                <label htmlFor="psirt-product-team">PSIRT-Team</label>
                {teams.length === 0 ? (
                  <EmptyState
                    compact
                    title="Noch kein PSIRT-Team angelegt"
                    action={
                      canWrite ? (
                        <button type="button" className="small" onClick={onCreateTeam}>
                          Team anlegen
                        </button>
                      ) : undefined
                    }
                  />
                ) : (
                  <select
                    id="psirt-product-team"
                    value={values.PsirtTeamID ?? ''}
                    disabled={readOnly}
                    onChange={(event) =>
                      setValues((current) => ({
                        ...current,
                        PsirtTeamID: event.target.value === '' ? null : Number(event.target.value),
                      }))
                    }
                  >
                    <option value="">— kein Team —</option>
                    {teams.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.label}
                      </option>
                    ))}
                  </select>
                )}
                {team ? (
                  <div className="psirt-team-preview">
                    <span>
                      <small>Meldestelle</small>
                      {team.values.MeldeEmail || <em>fehlt</em>}
                    </span>
                    <span>
                      <small>Koordinierendes CSIRT</small>
                      {team.values.CsirtKoordinator
                        ? `${team.values.CsirtKoordinator}${team.values.Mitgliedstaat ? ` · ${stateName(team.values.Mitgliedstaat)}` : ''}`
                        : <em>fehlt</em>}
                    </span>
                    <span>
                      <small>Erreichbarkeit</small>
                      {team.values.Erreichbarkeit || <em>nicht angegeben</em>}
                    </span>
                    {teamGaps.length > 0 ? (
                      <p className="psirt-team-gap">
                        Im Team fehlt noch: {teamGaps.join(', ')}. Das betrifft jedes Produkt dieses Teams.
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="psirt-grid psirt-grid-pairs">
              {text('OwnerName', 'Product Security Owner', { placeholder: 'Vor- und Nachname' })}
              {text('OwnerEmail', 'E-Mail', { type: 'email' })}
              {text('VertretungName', 'Stellvertretung', {
                placeholder: team?.values.VertretungName
                  ? `${team.values.VertretungName} (vom Team)`
                  : 'Vor- und Nachname',
              })}
              {text('VertretungEmail', 'E-Mail', { type: 'email' })}
            </div>
          </section>

          <section className="psirt-section">
            <header>
              <h3>CRA-Produktkategorie</h3>
              <p>Entscheidet über das Konformitätsbewertungsverfahren (Art. 32, Anhang III/IV).</p>
            </header>
            <div className="psirt-kategorien" role="radiogroup" aria-label="CRA-Produktkategorie">
              {KATEGORIE_ORDER.map((code) => {
                const on = values.CraKategorie === code;
                return (
                  <label key={code} className={`psirt-kategorie${on ? ' on' : ''}`} data-kategorie={code}>
                    <input
                      type="radio"
                      name="psirt-kategorie"
                      value={code}
                      checked={on}
                      disabled={readOnly || kategorien.length === 0}
                      onChange={() => setValues((current) => ({ ...current, CraKategorie: code }))}
                    />
                    <strong>{KATEGORIE_SHORT[code]}</strong>
                    <span>{KATEGORIE_FUNDSTELLE[code]}</span>
                    <small>{kategorieFolgen[code]}</small>
                  </label>
                );
              })}
            </div>
            {kategorien.length === 0 ? (
              <p className="hint psirt-warn-text">
                Die Produktkategorien fehlen noch in CMDBuild. Ein Administrator muss das Datenmodell ergänzen.
              </p>
            ) : null}
          </section>

          <section className="psirt-section">
            <header>
              <h3>Supportzeitraum</h3>
              <p>
                Mindestens {minYears} Jahre, außer die erwartete Nutzungsdauer ist kürzer. Das Enddatum muss
                beim Kauf erkennbar sein (Art. 13 Abs. 8, Anhang II Nr. 7).
              </p>
            </header>
            <div className="psirt-support">
              <div className="field">
                <label htmlFor="psirt-product-SupportEnde">Support bis</label>
                <input
                  id="psirt-product-SupportEnde"
                  type="date"
                  value={values.SupportEnde ?? ''}
                  disabled={readOnly}
                  onChange={(event) => set('SupportEnde', event.target.value)}
                />
              </div>
              {canWrite ? (
                <div className="psirt-quick" aria-label="Schnellauswahl">
                  {basis ? (
                    <button
                      type="button"
                      className="small"
                      disabled={readOnly}
                      onClick={() => set('SupportEnde', addYears(basis, minYears))}
                    >
                      {minYears} Jahre ab Erst-Release
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="small"
                    disabled={readOnly}
                    onClick={() => set('SupportEnde', addYears(today, minYears))}
                  >
                    {minYears} Jahre ab heute
                  </button>
                  <button
                    type="button"
                    className="small"
                    disabled={readOnly}
                    onClick={() => set('SupportEnde', addYears(today, 10))}
                  >
                    10 Jahre ab heute
                  </button>
                </div>
              ) : null}
            </div>
            {supportEnde ? (
              <p className={`psirt-support-info${tooShort || (remaining !== null && remaining < 0) ? ' warn' : ''}`}>
                {remaining !== null && remaining < 0
                  ? `Abgelaufen seit ${fmtSpan(remaining)}, keine Sicherheitsupdates mehr zugesagt`
                  : `Noch ${fmtSpan(remaining ?? 0)} ab heute`}
                {spanFromRelease !== null && basis
                  ? ` · ${fmtSpan(spanFromRelease)} ab Erst-Release am ${fmtDayIso(basis)}`
                  : ''}
                {tooShort ? `. Unter ${minYears} Jahren: Begründung in der Bemerkung festhalten.` : ''}
              </p>
            ) : basis ? (
              <p className="psirt-support-info">Erst-Release am {fmtDayIso(basis)}.</p>
            ) : null}
          </section>

          <section className="psirt-section">
            <header>
              <h3>Sicherheitsupdates</h3>
              <p>Kostenlos, getrennt von Funktionsupdates und möglichst automatisch (Anhang I Teil II Nr. 7–8).</p>
            </header>
            <div className="field">
              <label htmlFor="psirt-product-UpdateKanal">Wie erreichen Updates die Kunden?</label>
              <textarea
                id="psirt-product-UpdateKanal"
                rows={3}
                value={values.UpdateKanal ?? ''}
                placeholder="z. B. Signierte Firmware-Pakete im Kundenportal, Benachrichtigung per E-Mail an den Betreiber, Einspielen durch den Service vor Ort."
                disabled={readOnly}
                onChange={(event) => set('UpdateKanal', event.target.value)}
              />
            </div>
          </section>

          <section className="psirt-section">
            <header>
              <h3>Überprüfung</h3>
              <p>Einmal im Jahr bestätigen, dass Personen und Zeiträume noch stimmen.</p>
            </header>
            <div className="psirt-support">
              <div className="field">
                <label htmlFor="psirt-product-GeprueftAm">Zuletzt geprüft am</label>
                <input
                  id="psirt-product-GeprueftAm"
                  type="date"
                  value={values.GeprueftAm ?? ''}
                  max={today}
                  disabled={readOnly}
                  onChange={(event) => set('GeprueftAm', event.target.value)}
                />
              </div>
              {canWrite ? (
                <div className="psirt-quick">
                  <button
                    type="button"
                    className="small"
                    disabled={readOnly || values.GeprueftAm === today}
                    onClick={() => set('GeprueftAm', today)}
                  >
                    Heute geprüft
                  </button>
                </div>
              ) : null}
            </div>
            <div className="field">
              <label htmlFor="psirt-product-Bemerkung">Bemerkung</label>
              <textarea
                id="psirt-product-Bemerkung"
                rows={3}
                value={values.Bemerkung ?? ''}
                placeholder={tooShort ? 'Begründung für den kürzeren Supportzeitraum …' : undefined}
                disabled={readOnly}
                aria-invalid={(tooShort && !filled(values.Bemerkung)) || undefined}
                onChange={(event) => set('Bemerkung', event.target.value)}
              />
            </div>
          </section>
        </div>

        <PsirtChecklist
          title="CRA-Anforderungen"
          items={checklist}
          footnote="Die endgültige Bewertung erscheint nach dem Speichern in der Übersicht und im CRA-Check."
        />
      </form>
    </Modal>
  );
}

export default ProductFormModal;
